import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { Checkbox } from '../ui/checkbox'
import { Alert, AlertDescription } from '../ui/alert'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { useCan } from '../../contexts/PermissionsContext'
import { useFormatters } from '../../lib/format'
import {
  useAcceptRentReview,
  useCreateRentReview,
  useRejectRentReview,
  type RentReviewOutcome,
  type RentReviewProposal,
} from '../../api/leaseRent'

const MANUAL = ['statutory_notice', 'market_review', 'agreed_review']

/** What the dialog shows: the engine's dry run, or a stored proposal. */
type Shown = { outcome: RentReviewOutcome; proposal?: undefined } | { proposal: RentReviewProposal; outcome?: undefined }

interface Props {
  org: string
  contractUid: string
  shown: Shown
  buildingUid?: string | null
  onClose: () => void
  onShowReviewTerms: () => void
  onShowAnchor: () => void
}

/**
 * The rent review dialog (spec 2026-10-25-08 step 9): the whole computation,
 * then one decision, as the agent run proposals decide one item at a time
 * (patterns §49). A dry run is stored as a proposal when the manager decides.
 */
export function RentReviewDialog({ org, contractUid, shown, buildingUid, onClose, onShowReviewTerms, onShowAnchor }: Props) {
  const { t } = useTranslation('contracts')
  const { formatDate, formatMoney } = useFormatters()
  const canWrite = useCan('contracts.write')
  const create = useCreateRentReview()
  const accept = useAcceptRentReview()
  const reject = useRejectRentReview()
  const [proposal, setProposal] = useState<RentReviewProposal | undefined>(shown.proposal)
  const [note, setNote] = useState('')
  const [rejecting, setRejecting] = useState(false)
  const [confirmUnknown, setConfirmUnknown] = useState(false)
  const [manualRent, setManualRent] = useState('')
  const [servedOn, setServedOn] = useState('')
  const [evidence, setEvidence] = useState<string[]>([])
  const [error, setError] = useState('')

  const outcome = shown.outcome
  const view = proposal ?? outcome
  const kind = proposal ? proposal.status : outcome!.kind
  const mechanism = view?.mechanism ?? ''
  const currency = view?.currency ?? 'EUR'
  const inputs = view?.inputs
  const missing = outcome && !proposal ? outcome.missing : []
  const manual = !proposal && MANUAL.includes(mechanism) && missing.includes('proposed_rent_missing')
  const decided = proposal && ['accepted', 'rejected'].includes(proposal.status)
  const blocked = !proposal && outcome && ['missing_input', 'not_configured', 'not_applicable'].includes(outcome.kind) && !manual
  const frozen = kind === 'frozen'
  const foreclosed = kind === 'foreclosed'
  const unknownFreeze = view?.freeze_check === 'unknown'

  const docs = $api.useQuery('get', '/api/v1/organizations/{orgCode}/documents', {
    params: { path: { orgCode: org }, query: { contract_uid: contractUid, limit: 50 } },
  }, { enabled: manual })
  const docRows = (((docs.data as { data?: { uid: string; name?: string }[] } | undefined)?.data) ?? [])

  const fail = (e: unknown) => {
    const p = e as { title?: string; code?: string; fields?: { name: string; message: string }[] }
    setError(p.fields?.length ? p.fields.map((f) => t(`lease.rent_review.missing_codes.${f.message}` as any, { defaultValue: f.message })).join(', ') : (p.title ?? t('lease.rent_review.failed')))
  }

  // A dry run becomes a proposal the moment the manager decides on it.
  const stored = async (): Promise<RentReviewProposal | undefined> => {
    if (proposal) return proposal
    const body: Record<string, unknown> = {}
    if (manual) {
      body.mechanism = mechanism
      if (manualRent) body.proposed_rent_minor = Math.round(Number(manualRent.replace(',', '.')) * 100)
      if (servedOn) body[mechanism === 'agreed_review' ? 'agreed_on' : 'served_on'] = servedOn
      if (evidence.length) body.evidence_document_uids = evidence
    }
    const p = await create.mutateAsync({ params: { path: { orgCode: org, contractUid } }, body: body as never })
    setProposal(p)
    return p
  }

  const run = async (fn: () => Promise<void>) => {
    setError('')
    try {
      await fn()
    } catch (e) {
      fail(e)
    }
  }

  const doAccept = () => run(async () => {
    const p = await stored()
    if (!p) return
    const out = await accept.mutateAsync({
      params: { path: { orgCode: org, contractUid, proposalUid: p.uid } },
      body: { confirm_freeze_unknown: confirmUnknown },
    })
    setProposal(out)
  })

  const doReject = () => run(async () => {
    const p = await stored()
    if (!p) return
    const out = await reject.mutateAsync({ params: { path: { orgCode: org, contractUid, proposalUid: p.uid } }, body: { note } })
    setProposal(out)
    setRejecting(false)
  })

  const doPropose = () => run(async () => { await stored() })

  const pending = create.isPending || accept.isPending || reject.isPending
  const money = (v?: number | null) => (v === null || v === undefined ? '—' : formatMoney(v, currency))
  const day = (v?: string | null) => (v ? formatDate(v) : '—')

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="rent-review-dialog">
        <DialogHeader>
          <DialogTitle>{t('lease.rent_review.dialog_title')}</DialogTitle>
          <DialogDescription>
            <Badge variant="outline" data-testid="rent-review-mechanism" data-mechanism={mechanism}>
              {mechanism ? t(`lease.rent_review.mechanisms.${mechanism}` as any) : t('lease.rent_review.no_mechanism')}
            </Badge>{' '}
            <Badge variant={kind === 'accepted' ? 'success' : kind === 'proposal' || kind === 'proposed' ? 'secondary' : 'warning'} data-testid="rent-review-status">
              {t(`lease.rent_review.kinds.${kind}` as any)}
            </Badge>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          {blocked && outcome && (
            <Alert data-testid="rent-review-missing-panel">
              <AlertDescription className="space-y-2">
                <p>{t(`lease.rent_review.blocked.${outcome.kind}` as any)}</p>
                {outcome.reason_code && <p>{t(`lease.rent_review.reasons.${outcome.reason_code}` as any, { defaultValue: outcome.reason_code })}</p>}
                {outcome.missing.length > 0 && (
                  <ul className="list-disc pl-5">
                    {outcome.missing.map((m) => (
                      <li key={m} data-testid={`rent-review-missing-${m}`}>{t(`lease.rent_review.missing_codes.${m}` as any, { defaultValue: m })}</li>
                    ))}
                  </ul>
                )}
                {(outcome.missing.includes('review_terms_not_confirmed') || outcome.missing.includes('index_not_published')) && (
                  <Button variant="link" className="h-auto p-0" onClick={onShowReviewTerms} data-testid="rent-review-missing-review-terms-link">
                    {t('lease.rent_review.open_review_terms')}
                  </Button>
                )}
                {outcome.missing.includes('primary_lot_missing') && (
                  <Button variant="link" className="h-auto p-0" onClick={onShowAnchor} data-testid="rent-review-missing-anchor-link">
                    {t('lease.rent_review.open_anchor')}
                  </Button>
                )}
              </AlertDescription>
            </Alert>
          )}

          {!blocked && view && (
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2">
              <dt className="text-muted-foreground">{t('lease.rent_review.review_date')}</dt>
              <dd data-testid="rent-review-review-date">{day(view.review_date)}</dd>
              <dt className="text-muted-foreground">{t('lease.rent_review.current_rent')}</dt>
              <dd className="tabular-nums" data-testid="rent-review-current-rent">{money(view.current_rent_minor)}</dd>
              {inputs?.old_index && (
                <>
                  <dt className="text-muted-foreground">{t('lease.rent_review.old_index')}</dt>
                  <dd data-testid="rent-review-index-old">
                    {inputs.old_index.index_code.toUpperCase()} {inputs.old_index.period} : {inputs.old_index.value}{' '}
                    <span className="text-muted-foreground">({t(`lease.rent_review.index_sources.${inputs.old_index.source}` as any)})</span>
                  </dd>
                </>
              )}
              {inputs?.new_index && (
                <>
                  <dt className="text-muted-foreground">{t('lease.rent_review.new_index')}</dt>
                  <dd data-testid="rent-review-index-new">
                    {inputs.new_index.index_code.toUpperCase()} {inputs.new_index.period} : {inputs.new_index.value}{' '}
                    <span className="text-muted-foreground">({t(`lease.rent_review.index_sources.${inputs.new_index.source}` as any)})</span>
                  </dd>
                  <dt className="text-muted-foreground">{t('lease.rent_review.formula')}</dt>
                  <dd className="tabular-nums" data-testid="rent-review-formula">
                    {money(view.current_rent_minor)} × {inputs.new_index.value} / {inputs.old_index?.value} = {money(inputs.formula_rent_minor)}
                  </dd>
                </>
              )}
              {(inputs?.bounds ?? []).length > 0 && (
                <>
                  <dt className="text-muted-foreground">{t('lease.rent_review.bounds')}</dt>
                  <dd data-testid="rent-review-bounds">
                    {(inputs?.bounds ?? []).map((b) => (
                      <div key={b.kind + (b.rule_code ?? '')}>
                        {t(`lease.rent_review.bound_kinds.${b.kind}` as any)} {b.pct} % : {money(b.from_minor)} → {money(b.to_minor)}
                        {b.legal_basis && <span className="text-muted-foreground"> ({b.legal_basis})</span>}
                      </div>
                    ))}
                  </dd>
                </>
              )}
              {inputs?.served_on && (
                <>
                  <dt className="text-muted-foreground">{t('lease.rent_review.served_on')}</dt>
                  <dd>{day(inputs.served_on)}</dd>
                </>
              )}
              {!frozen && !foreclosed && view.proposed_rent_minor !== null && view.proposed_rent_minor !== undefined && (
                <>
                  <dt className="font-medium">{t('lease.rent_review.proposed_rent')}</dt>
                  <dd className="font-medium tabular-nums" data-testid="rent-review-proposed-rent">{money(view.proposed_rent_minor)}</dd>
                  <dt className="text-muted-foreground">{t('lease.rent_review.effective_on')}</dt>
                  <dd data-testid="rent-review-effective-on">{day(view.effective_on)}</dd>
                </>
              )}
            </dl>
          )}

          {manual && (
            <div className="grid grid-cols-2 gap-3" data-testid="rent-review-manual-form">
              <div className="space-y-1">
                <Label htmlFor="rent-review-manual-rent">{t('lease.rent_review.manual_rent')}</Label>
                <Input id="rent-review-manual-rent" inputMode="decimal" value={manualRent} onChange={(e) => setManualRent(e.target.value)}
                  data-testid="rent-review-manual-rent-input" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="rent-review-served-on">{t(mechanism === 'agreed_review' ? 'lease.rent_review.agreed_on' : 'lease.rent_review.served_on')}</Label>
                <Input id="rent-review-served-on" type="date" value={servedOn} onChange={(e) => setServedOn(e.target.value)}
                  data-testid="rent-review-served-on-input" />
              </div>
              {mechanism !== 'statutory_notice' && (
                <div className="col-span-2 space-y-1" data-testid="rent-review-evidence-picker">
                  <Label>{t('lease.rent_review.evidence')}</Label>
                  {docRows.length === 0 && <p className="text-muted-foreground">{t('lease.rent_review.evidence_none')}</p>}
                  {docRows.map((d) => (
                    <label key={d.uid} className="flex items-center gap-2">
                      <Checkbox checked={evidence.includes(d.uid)} data-testid={`rent-review-evidence-${d.uid}`}
                        onCheckedChange={(c) => setEvidence(c ? [...evidence, d.uid] : evidence.filter((x) => x !== d.uid))} />
                      {d.name ?? d.uid}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {frozen && view && (
            <Alert variant="warning" data-testid="rent-review-freeze-panel">
              <AlertDescription className="space-y-1">
                <p>{t('lease.rent_review.frozen_reason', {
                  class: view.freeze_snapshot?.energy_class ?? '?',
                  provenance: t(`lease.rent_review.provenances.${view.freeze_snapshot?.provenance ?? 'unknown'}` as any, { defaultValue: view.freeze_snapshot?.provenance }),
                })}</p>
                {view.freeze_snapshot?.date_used && (
                  <p>{t(`lease.rent_review.frozen_date_${view.freeze_snapshot.date_kind ?? 'concluded'}` as any, { date: day(view.freeze_snapshot.date_used), cutoff: day(view.freeze_snapshot.applies_to_leases_from) })}</p>
                )}
                {view.freeze_snapshot?.legal_basis && <p className="text-muted-foreground">{view.freeze_snapshot.legal_basis}</p>}
                <p className="font-medium" data-testid="rent-review-lost-rent">
                  {t('lease.rent_review.lost_rent', { amount: money(view.lost_rent_minor_per_year) })}
                </p>
                {buildingUid ? (
                  <Link to="/$org/assets/$assetUid" params={{ org, assetUid: buildingUid }} search={{ tab: 'capex' } as never}
                    className="underline" data-testid="rent-review-capex-link">
                    {t('lease.rent_review.capex_link')}
                  </Link>
                ) : (
                  <Link to="/$org/capex-plans" params={{ org }} className="underline" data-testid="rent-review-capex-link">
                    {t('lease.rent_review.capex_link')}
                  </Link>
                )}
              </AlertDescription>
            </Alert>
          )}

          {foreclosed && view && (
            <Alert variant="warning" data-testid="rent-review-foreclosed-panel">
              <AlertDescription>
                {t('lease.rent_review.foreclosed', {
                  date: day(outcome?.window_closes_on ?? null),
                  months: inputs?.window_months ?? 12,
                })}
              </AlertDescription>
            </Alert>
          )}

          {unknownFreeze && !decided && !frozen && (
            <Alert variant="warning" data-testid="rent-review-freeze-unknown">
              <AlertDescription className="space-y-2">
                <p>{t('lease.rent_review.freeze_unknown')}</p>
                <label className="flex items-center gap-2">
                  <Checkbox checked={confirmUnknown} onCheckedChange={(c) => setConfirmUnknown(!!c)} data-testid="rent-review-freeze-unknown-checkbox" />
                  {t('lease.rent_review.freeze_unknown_confirm')}
                </label>
              </AlertDescription>
            </Alert>
          )}

          {proposal?.status === 'accepted' && (
            <div className="flex flex-wrap gap-3" data-testid="rent-review-accepted">
              <span>{t('lease.rent_review.accepted', { date: day(proposal.effective_on) })}</span>
              {proposal.notice_document_uid && (
                <Link to="/$org/documents/$docUid" params={{ org, docUid: proposal.notice_document_uid }} className="underline"
                  data-testid="rent-review-notice-link">
                  {t('lease.rent_review.notice_link')}
                </Link>
              )}
            </div>
          )}
          {proposal?.status === 'rejected' && <p data-testid="rent-review-rejected">{t('lease.rent_review.rejected', { note: proposal.decision_note ?? '' })}</p>}

          {rejecting && (
            <div className="space-y-1">
              <Label htmlFor="rent-review-reject-note">{t('lease.rent_review.reject_note')}</Label>
              <Textarea id="rent-review-reject-note" value={note} onChange={(e) => setNote(e.target.value)} data-testid="rent-review-reject-note-input" />
            </div>
          )}

          {error && (
            <Alert variant="destructive" data-testid="rent-review-error">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          {canWrite && !decided && !blocked && (
            <>
              {manual ? (
                <Button onClick={() => void doPropose()} disabled={pending} data-testid="rent-review-propose-button">{t('lease.rent_review.propose')}</Button>
              ) : rejecting ? (
                <Button variant="outline" onClick={() => void doReject()} disabled={pending || note.trim() === ''} data-testid="rent-review-reject-confirm-button">
                  {frozen || foreclosed ? t('lease.rent_review.acknowledge') : t('lease.rent_review.reject')}
                </Button>
              ) : (
                <Button variant="outline" onClick={() => setRejecting(true)} disabled={pending} data-testid="rent-review-reject-button">
                  {frozen || foreclosed ? t('lease.rent_review.acknowledge') : t('lease.rent_review.reject')}
                </Button>
              )}
              {!manual && !frozen && !foreclosed && (
                <Button onClick={() => void doAccept()} disabled={pending || (unknownFreeze && !confirmUnknown)} data-testid="rent-review-accept-button">
                  {t('lease.rent_review.accept')}
                </Button>
              )}
            </>
          )}
          <Button variant="ghost" onClick={onClose} data-testid="rent-review-close-button">{t('lease.rent_review.close')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
